/**
 * Remastered cave fabric: dirt, brick, steel and their relatives. Dirt is one seamless texture four
 * cells square; brick and steel come in four variants whose edges match, so neighbouring cells join
 * cleanly while the cave never looks stamped out. Pure.
 */

import type { RampSet } from './color';
import { Px } from './px';
import { hash3, lambert, normalize, step, tileNoise } from './shade';

/** Dirt is one seamless 64 x 64 texture (4 x 4 cells); each cell draws its own piece of it. */
export const DIRT_TILES = 16;

function height(x: number, y: number): number {
  return tileNoise(x, y, 4, 16, 11) * 0.62 + tileNoise(x, y, 2, 32, 12) * 0.38;
}

/** Piece `variant` (0-15, by cell position) of lumpy earth with dark crevices and a pebble or two. */
export function dirt(r: RampSet, variant: number): Px {
  const p = new Px();
  const ox = (variant & 3) * 16;
  const oy = ((variant >> 2) & 3) * 16;
  for (let y = 0; y < 16; y++) {
    for (let x = 0; x < 16; x++) {
      const h = height(x + ox, y + oy);
      if (h < 0.3) {
        p.set(x, y, r.earth[0]);
        continue;
      }
      const [hx, hy] = [x + ox, y + oy];
      const n = normalize([(height(hx - 1, hy) - height(hx + 1, hy)) * 3, (height(hx, hy - 1) - height(hx, hy + 1)) * 3, 1]);
      p.set(x, y, r.earth[step(lambert(n) * 0.9 + (h - 0.5) * 0.25, x, y, 0, 3)] ?? r.earth[2]);
    }
  }
  const pebbles = hash3(variant, 1, 7) % 3;
  for (let i = 0; i < pebbles; i++) {
    const px = 2 + (hash3(variant, i, 8) % 11);
    const py = 2 + (hash3(variant, i, 9) % 11);
    p.set(px, py, r.rock[3]);
    p.set(px + 1, py, r.rock[2]);
    p.set(px, py + 1, r.rock[1]);
    p.set(px + 1, py + 1, r.rock[0]);
  }
  return p;
}

/** Brick courses four pixels tall, offset every other course, each brick bevelled and toned. */
export function brick(r: RampSet, variant: number, glow = -1): Px {
  const p = new Px();
  for (let y = 0; y < 16; y++) {
    const course = y >> 2;
    const ly = y & 3;
    const offset = (course & 1) * 4;
    for (let x = 0; x < 16; x++) {
      const bx = (x + offset) & 7;
      const id = ((x + offset) >> 3) & 1;
      if (ly === 3 || bx === 7) {
        p.set(x, y, r.earth[0]);
        continue;
      }
      const ramp = glow >= 0 && (course * 2 + id + glow) % 4 === 0 ? r.bright : r.rock;
      let shade = ly === 0 || bx === 0 ? 3 : ly === 2 || bx === 6 ? 1 : 2;
      if (hash3(course, id, 5) % 3 === 0) shade = Math.max(0, shade - 1);
      if (shade === 2 && hash3(x, y, 6) % 9 === 0) shade = 1;
      p.set(x, y, ramp[shade] ?? ramp[2]);
    }
  }
  if (variant === 1) for (const [x, y] of [[9, 5], [10, 6], [10, 5]] as const) p.set(x, y, r.ink);
  if (variant === 2) for (const [x, y] of [[3, 9], [4, 10]] as const) p.set(x, y, r.rock[1]);
  return p;
}

/** Bevelled steel plates: lit top and left, shadowed bottom and right, four rivets. */
export function steel(r: RampSet, variant: number): Px {
  const p = new Px();
  for (let y = 0; y < 16; y++) {
    for (let x = 0; x < 16; x++) {
      let shade = 2;
      if (x === 15 || y === 15) shade = 0;
      else if (x === 0 || y === 0) shade = 4;
      else if (x === 1 || y === 1) shade = 3;
      else if (x === 14 || y === 14) shade = 1;
      else if (hash3(y, variant, 3) % 4 === 0 && (x * 3 + y + variant) % 9 < 4) shade = 3;
      p.set(x, y, r.rock[shade] ?? r.rock[2]);
    }
  }
  for (const [x, y] of [[3, 3], [11, 3], [3, 11], [11, 11]] as const) {
    p.set(x, y, r.rock[4]);
    p.set(x + 1, y, r.rock[3]);
    p.set(x, y + 1, r.rock[3]);
    p.set(x + 1, y + 1, r.rock[0]);
  }
  return p;
}

/** A running magic wall: bricks light up in turn, with sparks. */
export function magicWall(r: RampSet, frame: number): Px {
  const p = brick(r, 0, frame);
  for (let i = 0; i < 3; i++) {
    const x = hash3(frame, i, 21) % 16;
    const y = hash3(frame, i, 22) % 16;
    p.set(x, y, r.bright[4]);
  }
  return p;
}

/** An expanding wall: brick with bright arrowheads pointing out both sides. */
export function expandingWall(r: RampSet): Px {
  const p = brick(r, 3);
  for (const y of [5, 9]) {
    p.set(0, y + 1, r.bright[4]);
    p.set(1, y, r.bright[3]);
    p.set(1, y + 2, r.bright[3]);
    p.set(15, y + 1, r.bright[4]);
    p.set(14, y, r.bright[3]);
    p.set(14, y + 2, r.bright[3]);
  }
  return p;
}

/** The cover: steel plates with glints flickering across them. */
export function cover(r: RampSet, frame: number): Px {
  const p = steel(r, frame);
  for (let y = 2; y < 14; y++) {
    for (let x = 2; x < 14; x++) {
      const h = hash3(x, y, 60 + frame) % 28;
      if (h === 0) p.set(x, y, r.bright[4]);
      else if (h === 1) p.set(x, y, r.rock[4]);
      else if (h === 2) p.set(x, y, r.rock[1]);
    }
  }
  return p;
}
